import React, { useEffect, useState } from "react";
import Loader from "react-loader-spinner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from "recharts";
import {
  LabelContainer,
  Label,
  ChartContainer,
  Title,
  Container,
} from "./ChartTile.styles";
import { ChartTileProps } from "./ChartTile.types";
import { colorTheme } from "../../theme";

const AvgHrsColor = {
  red: 60,
  yellow: 50,
  green: 40,
} as const;
const nonFieldOffset = -10;

export const ChartTile: React.FC<ChartTileProps> = ({
  title,
  data,
  type,
  dataField,
}) => {
  const [max, setMax] = useState<number>(0);
  const [sortedData, setSortedData] = useState<any>([]);
  const [intervals, setIntervals] = useState<Array<number>>([]);

  useEffect(() => {
    data.length ? formatData(data, type) : null;
  }, [data]);

  const formatData = (data: any, type: any) => {
    let newData: any = [];
    let max = data[0];
    const intervals = [0];

    if (type === "checkins") {
      newData = data.slice(-10);
      max = newData[newData.length - 1][dataField];
    } else if (type === "overtimeWeeks" || type === "timeOff") {
      newData = data.slice(0, 10);
      max = data[0][dataField];
    } else if (type === "leaderScore") {
      const top10 = data.slice(0, 10);
      const bottom10 = data.slice(-10);

      let bottomCount = bottom10.length;
      for (let i = top10.length - 1; i >= 0; i--) {
        if (top10[i].hours < 50 && bottom10[bottomCount - 1] < 40) {
          top10[i] = bottom10[bottomCount];
          bottomCount--;
        } else {
          break;
        }
      }

      newData = top10;
      max = Math.ceil(data[0][dataField]);
    }

    const interval = max / 6;
    for (let i = 0; i < 6; i++) {
      intervals.push(intervals[intervals.length - 1] + interval);
    }

    setMax(max);
    setSortedData(newData);
    setIntervals(intervals);
  };

  const handleBarFill = (entry: any, type: string) => {
    if (type === "overtimeWeeks") {
      if (entry[dataField] >= 15) {
        return colorTheme.chartRed;
      } else if (entry[dataField] >= 3 && entry[dataField] <= 14) {
        return colorTheme.chartYellow;
      } else if (entry[dataField] >= 0 && entry[dataField] <= 2) {
        return colorTheme.chartGreen;
      }
    } else if (type === "avgLeaderScore") {
      if (entry.team === "Field") {
        if (entry[dataField] >= 50 || entry[dataField] < 40) {
          return colorTheme.chartRed;
        } else if (entry[dataField] >= 51 && entry[dataField] <= 60) {
          return colorTheme.chartYellow;
        } else if (entry[dataField] >= 41 && entry[dataField] <= 50) {
          return colorTheme.chartGreen;
        }
      } else if (entry.team === "Non-field") {
        if (entry[dataField] >= 51 || entry[dataField] < 29) {
          return colorTheme.chartRed;
        } else if (entry[dataField] >= 41 && entry[dataField] <= 50) {
          return colorTheme.chartYellow;
        } else if (entry[dataField] >= 30 && entry[dataField] <= 40) {
          return colorTheme.chartGreen;
        }
      }
    } else if (type === "leaderScore") {
      let val = entry[dataField];
      if (!entry.isField) val += nonFieldOffset;

      if (val > AvgHrsColor.red || val < AvgHrsColor.green) {
        return colorTheme.chartRed;
      } else if (val >= AvgHrsColor.yellow) {
        return colorTheme.chartYellow;
      } else if (val >= AvgHrsColor.green) {
        return colorTheme.chartGreen;
      }
    } else if (type === "checkins") {
      if (entry.type >= 15) {
        return colorTheme.chartRed;
      } else if (entry.type >= 3 && entry.type <= 14) {
        return colorTheme.chartYellow;
      } else if (entry.type >= 0 && entry.type <= 2) {
        return colorTheme.chartGreen;
      }
    } else if (type === "timeOff") {
      if (entry[dataField] >= 15) {
        return colorTheme.chartRed;
      } else if (entry[dataField] >= 3 && entry[dataField] <= 14) {
        return colorTheme.chartYellow;
      } else if (entry[dataField] >= 0 && entry[dataField] <= 2) {
        return colorTheme.chartGreen;
      }
    }
  };

  return (
    <Container>
      {sortedData === null ? (
        <>
          <Loader type="Oval" color="#00BFFF" height={40} width={40} />
        </>
      ) : (
        <>
          <Title>{title}</Title>
          <ChartContainer>
            <BarChart
              data={sortedData}
              width={260}
              height={250}
              barSize={5}
              layout="vertical"
              margin={{ top: 0, right: 1, left: -38, bottom: -6 }}
            >
              <CartesianGrid
                horizontal={false}
                strokeWidth={1}
                strokeDasharray="0"
                stroke="#F4F5F4"
                height={250}
                y={10}
              />
              <YAxis
                id="y-axis"
                type="category"
                dataKey={"name"}
                tickMargin={0}
                tickLine={false}
                axisLine={false}
                interval={0}
                width={150}
                tick={{
                  fontFamily: "Open Sans",
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.2px",
                  color: "#373F41",
                }}
              />
              <XAxis
                type="number"
                dataKey={dataField}
                tickLine={false}
                axisLine={false}
                interval={0}
                domain={[0, "datamax"]}
                ticks={intervals}
                hide={true}
              />
              <Bar dataKey={dataField} radius={[0, 10, 10, 0]}>
                {sortedData.map((entry: any, index: any) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={handleBarFill(entry, type)}
                  />
                ))}
              </Bar>
            </BarChart>
            <LabelContainer>
              <Label>0</Label>
              <Label>{max}</Label>
            </LabelContainer>
          </ChartContainer>
        </>
      )}
    </Container>
  );
};
